/** Data levels in plain words (/data-levels): what each level means and what it allows. */
export default {
  en: {
    title: "Data levels",
    subtitle: "Every document, conversation, agent and model has a data level. It decides where the data may go and who may see it.",
    what: "What this means",
    levels: {
      public: { name: "Public", meaning: "Anyone may see it.", example: "Opening hours, published prices, a public FAQ." },
      internal: { name: "Internal", meaning: "Anyone in your organisation may see it.", example: "Internal policies, the staff handbook, team notes." },
      confidential: { name: "Confidential", meaning: "A limited group may see it.", example: "Invoices, contracts, salaries, customer records." },
      restricted: { name: "Restricted", meaning: "Only named people with the highest access may see it.", example: "Board papers, security incidents, health records." },
    },
    rulesTitle: "What the level decides",
    rules: {
      models: { title: "Which AI models may read it", body: "Data above the online limit is only sent to models on our own servers. The online limit is {{online}} on this platform{{orgPart}}." },
      orgPart: ", and your organisation allows up to {{org}}",
      orgOff: "; your organisation does not use online models",
      people: { title: "Who may find it", body: "People find documents and answers only up to their own access level. A person with Internal access never sees a Confidential document, not even in an answer." },
      agents: { title: "What an agent may handle", body: "Each agent has a highest data level. It refuses data above it, and its answers carry the level of the most sensitive data it used." },
      cache: { title: "Saved answers", body: "Answers are only saved and reused for data at or below the saved-answers limit, so sensitive answers are never served to someone else." },
    },
    whereTitle: "Where you see it",
    where: {
      agent: "Agent overview › About: the agent's highest data level.",
      knowledge: "Knowledge: each collection's level and who can find it.",
      request: "A request's story: which model answered, and why a local model was used.",
      settings: "Administration › Organisation: the highest level your organisation sends to online models.",
    },
    link: "What data levels mean",
    online: "online models allowed",
    localOnly: "only models on our own servers",
    // ADR-0108: each AI step's data level in the Builder.
    step: {
      online: "This step sees {{level}} data → a fast online model answers it.",
      local: "This step sees {{level}} data → it stays on your organisation's own model (simpler answers).",
      because: "Because it reads {{what}}.",
      reason: { question: "the question", earlier_steps: "the results of earlier steps", step: "what “{{step}}” gives", unknown: "a value whose level isn't known" },
      mayRise: "In a conversation, {{what}} can raise it up to {{ceiling}}; it then moves to your own model.",
      rise: { history: "earlier answers", attachments: "attached documents", memory: "what it remembers" },
      input: {
        label: "What people send this agent",
        help: "Each AI step starts from this level, then rises with what it reads. Left empty, what a person types counts as Internal and what another system sends counts as the agent's own level. Choose Public for a public website, or Confidential if people type sensitive data (then their questions stay on your own model).",
        usual: "Usual: Internal for people, the agent's level for other systems",
      },
    },
  },
  ar: {
    title: "مستويات البيانات",
    subtitle: "لكل مستند ومحادثة ووكيل ونموذج مستوى بيانات. يحدد إلى أين يمكن أن تذهب البيانات ومن يمكنه رؤيتها.",
    what: "ماذا يعني",
    levels: {
      public: { name: "عام", meaning: "يمكن لأي أحد رؤيتها.", example: "ساعات العمل، والأسعار المنشورة، والأسئلة الشائعة العامة." },
      internal: { name: "داخلي", meaning: "يمكن لأي شخص في جهتك رؤيتها.", example: "السياسات الداخلية، ودليل الموظفين، وملاحظات الفريق." },
      confidential: { name: "سري", meaning: "تراها مجموعة محدودة فقط.", example: "الفواتير، والعقود، والرواتب، وسجلات العملاء." },
      restricted: { name: "مقيّد", meaning: "لا يراها إلا أشخاص محددون بأعلى صلاحية.", example: "أوراق مجلس الإدارة، والحوادث الأمنية، والسجلات الصحية." },
    },
    rulesTitle: "ما الذي يحدده المستوى",
    rules: {
      models: { title: "أي نماذج الذكاء الاصطناعي يمكنها قراءتها", body: "البيانات الأعلى من حد الإنترنت لا تُرسل إلا إلى نماذج على خوادمنا. حد الإنترنت في هذه المنصة هو {{online}}{{orgPart}}." },
      orgPart: "، وتسمح جهتك حتى {{org}}",
      orgOff: "؛ وجهتك لا تستخدم النماذج عبر الإنترنت",
      people: { title: "من يمكنه العثور عليها", body: "يعثر الناس على المستندات والإجابات حتى مستوى صلاحيتهم فقط. من لديه صلاحية داخلي لا يرى مستندًا سريًا أبدًا، ولا حتى ضمن إجابة." },
      agents: { title: "ما الذي يمكن للوكيل التعامل معه", body: "لكل وكيل أعلى مستوى بيانات. يرفض البيانات الأعلى منه، وتحمل إجاباته مستوى أكثر البيانات حساسية مما استخدمه." },
      cache: { title: "الإجابات المحفوظة", body: "لا تُحفظ الإجابات ويُعاد استخدامها إلا لبيانات في حد الإجابات المحفوظة أو أقل، فلا تُقدَّم إجابة حساسة لشخص آخر أبدًا." },
    },
    whereTitle: "أين تراه",
    where: {
      agent: "نظرة على الوكيل › عن الوكيل: أعلى مستوى بيانات للوكيل.",
      knowledge: "المعرفة: مستوى كل مجموعة ومن يمكنه العثور عليها.",
      request: "قصة الطلب: أي نموذج أجاب، ولماذا استُخدم نموذج محلي.",
      settings: "الإدارة › الجهة: أعلى مستوى ترسله جهتك إلى النماذج عبر الإنترنت.",
    },
    link: "ماذا تعني مستويات البيانات",
    online: "النماذج عبر الإنترنت مسموح بها",
    localOnly: "نماذج على خوادمنا فقط",
    step: {
      online: "ترى هذه الخطوة بيانات {{level}} ← يجيب عنها نموذج سريع عبر الإنترنت.",
      local: "ترى هذه الخطوة بيانات {{level}} ← تبقى على نموذج مؤسستك الخاص (إجابات أبسط).",
      because: "لأنها تقرأ {{what}}.",
      reason: { question: "السؤال", earlier_steps: "نتائج الخطوات السابقة", step: "ما تعطيه «{{step}}»", unknown: "قيمة مستواها غير معروف" },
      mayRise: "في المحادثة، يمكن أن ترفعه {{what}} حتى {{ceiling}}؛ فتنتقل حينها إلى نموذجك الخاص.",
      rise: { history: "الإجابات السابقة", attachments: "المستندات المرفقة", memory: "ما يتذكره" },
      input: {
        label: "ما يرسله الناس إلى هذا الوكيل",
        help: "تبدأ كل خطوة ذكاء اصطناعي من هذا المستوى ثم يرتفع بحسب ما تقرؤه. إذا تُرك فارغًا يُعدّ ما يكتبه الشخص «داخليًا» وما يرسله نظام آخر بمستوى الوكيل نفسه. اختر «عامة» لموقع إلكتروني عام، أو «سرية» إذا كتب الناس بيانات حساسة (فتبقى أسئلتهم على نموذجك الخاص).",
        usual: "المعتاد: داخلية للأشخاص، ومستوى الوكيل للأنظمة الأخرى",
      },
    },
  },
};
